import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { UserProfile } from '../types';
import * as authApi from '../api/auth';
import { ApiError, setCsrfToken } from '../api/client';
import { StatePanel } from '../components/StatePanel';

interface AuthContextValue {
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  login: (login: string, password: string) => Promise<authApi.AuthStep | null>;
  verifyLogin: (step: authApi.AuthStep, code: string, trustDevice: boolean, rememberDevice: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [restoreFailed, setRestoreFailed] = useState(false);
  const authEpoch = useRef(0);
  const logoutStarted = useRef(false);
  const retryRestore = useRef(() => {});

  const login = useCallback(async (username: string, password: string) => {
    authEpoch.current += 1;
    logoutStarted.current = false;
    setIsLoading(true);
    try {
      const result = await authApi.login(username, password);
      if ('kind' in result) return result;
      setProfile(result.profile);
      return null;
    } finally { setIsLoading(false); }
  }, []);

  const verifyLogin = useCallback(async (step: authApi.AuthStep, code: string, trustDevice: boolean, rememberDevice: boolean) => {
    authEpoch.current += 1;
    logoutStarted.current = false;
    setIsLoading(true);
    try {
      const result = await authApi.verifyTotpLogin(step, code, trustDevice, rememberDevice);
      setProfile(result.profile);
    } finally { setIsLoading(false); }
  }, []);

  const clearSession = useCallback(() => { setProfile(null); setCsrfToken(null); }, []);
  const logout = useCallback(async () => {
    authEpoch.current += 1;
    logoutStarted.current = true;
    setIsInitializing(false);
    setIsLoading(true);
    try { await authApi.logout(); }
    catch (error) { if (!(error instanceof ApiError && error.status === 401)) throw error; }
    finally { clearSession(); setIsLoading(false); }
  }, [clearSession]);

  useEffect(() => {
    let restoring = false;
    let active = true;
    let retryTimer: number | undefined;
    let failures = 0;
    const recover = () => {
      if (!active || restoring || logoutStarted.current) return;
      window.clearTimeout(retryTimer);
      retryTimer = undefined;
      restoring = true;
      const epoch = authEpoch.current;
      setIsInitializing(true);
      void authApi.restoreSession().then((result) => {
        if (active && authEpoch.current === epoch) {
          setProfile(result.profile);
          setRestoreFailed(false);
          setIsInitializing(false);
          failures = 0;
        }
        else if (logoutStarted.current) setCsrfToken(null);
      }).catch((error: unknown) => {
        if (!active || authEpoch.current !== epoch) return;
        if (error instanceof ApiError && error.status === 401) {
          clearSession();
          setRestoreFailed(false);
          setIsInitializing(false);
        } else {
          setRestoreFailed(true);
          retryTimer = window.setTimeout(() => {
            if (authEpoch.current === epoch) recover();
          }, Math.min(30_000, 2_000 * 2 ** Math.min(failures++, 4)));
        }
      }).finally(() => { restoring = false; });
    };
    const retryWhenVisible = () => {
      if (retryTimer !== undefined && document.visibilityState === 'visible') recover();
    };
    retryRestore.current = recover;
    recover();
    window.addEventListener('korsaapp:unauthorized', recover);
    window.addEventListener('online', retryWhenVisible);
    document.addEventListener('visibilitychange', retryWhenVisible);
    return () => {
      active = false;
      window.clearTimeout(retryTimer);
      retryRestore.current = () => {};
      window.removeEventListener('korsaapp:unauthorized', recover);
      window.removeEventListener('online', retryWhenVisible);
      document.removeEventListener('visibilitychange', retryWhenVisible);
    };
  }, [clearSession]);

  useEffect(() => {
    if (!profile) return;
    const refresh = () => { if (document.visibilityState === 'visible') void authApi.refreshPersistentDevice().catch(() => {}); };
    const timer = window.setInterval(refresh, 60 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [profile]);

  const value = useMemo(() => ({ profile, isAuthenticated: Boolean(profile), isLoading, isInitializing,
    login, verifyLogin, logout }), [profile, isLoading, isInitializing, login, verifyLogin, logout]);
  return <AuthContext.Provider value={value}>{isInitializing && restoreFailed
    ? <main className="page-container"><StatePanel kind="error" title="Восстанавливаем соединение"
      description="Не удалось проверить сессию. Повторяем попытку автоматически."
      actionLabel="Повторить сейчас" onAction={() => retryRestore.current()} /></main>
    : children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
};
